import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { PixelTrackerService } from "#gen/pixel_tracker/v1/pixel_tracker_pb.js";
import type { CreateTagResponse, DeleteTagResponse, GenerateInlineTrackerFunctionMapResponse, GenerateInlineTrackerFunctionsResponse, GenerateStaticTagCodeResponse, GenerateTagCodeResponse, GetAllDomainsResponse, GetTagResponse, ListContextTemplatesResponse, ListTagsResponse, ListWorkspaceTagsResponse, Tag, UpdateTagResponse } from "#gen/pixel_tracker/v1/pixel_tracker_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { FieldMask } from "@bufbuild/protobuf/wkt";
import type { CallOptions, Client } from "@connectrpc/connect";

export type PixelList = {
	tags: Tag[];
	pageInfo?: PaginationPageInfo;
};

export class PixelTrackerClient {
	private client: Client<typeof PixelTrackerService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.pixels || "/", PixelTrackerService, options);
	}

	async deleteTag(props: { orgId: string; tagId: string }): Promise<DeleteTagResponse> {
		try {
			const res = await this.client.deleteTag(props);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createTag(tag: Tag): Promise<CreateTagResponse> {
		try {
			const res = await this.client.createTag({ tag });
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async generateStaticTagCode(props: { orgId: string; tagId: string }): Promise<GenerateStaticTagCodeResponse> {
		try {
			const res = await this.client.generateStaticTagCode(props);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async generateTagCode(props: { orgId: string; tagId: string }): Promise<GenerateTagCodeResponse> {
		try {
			const res = await this.client.generateTagCode(props);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async generateInlineTrackerFunctions(props: { orgId: string; tagId: string }): Promise<GenerateInlineTrackerFunctionsResponse> {
		try {
			const res = await this.client.generateInlineTrackerFunctions(props);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async generateInlineTrackerFunctionMap(props: { orgId: string; tagId: string }): Promise<GenerateInlineTrackerFunctionMapResponse> {
		try {
			const res = await this.client.generateInlineTrackerFunctionMap(props);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateTag(props: { tag?: Tag; mask?: FieldMask }): Promise<UpdateTagResponse> {
		try {
			const res = await this.client.updateTag(props);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listTags(props: { orgId: string; pagination?: PaginationArguments; displayName?: string }, options?: CallOptions): Promise<ListTagsResponse> {
		try {
			const res = await this.client.listTags(props, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getAllDomains(props: { orgId: string; workspaceId?: string; domain?: string; pagination?: PaginationArguments }, options?: CallOptions): Promise<GetAllDomainsResponse> {
		try {
			const res = await this.client.getAllDomains(props, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listWorkspaceTags(props: { orgId: string; workspaceId: string; pagination?: PaginationArguments }, options?: CallOptions): Promise<ListWorkspaceTagsResponse> {
		try {
			const res = await this.client.listWorkspaceTags(props, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getTag(props: { tagId: string; orgId?: string; workspaceId?: string }): Promise<GetTagResponse> {
		try {
			const res = await this.client.getTag(props);
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listContextTemplates(props: { orgId: string; workspaceId?: string }): Promise<ListContextTemplatesResponse> {
		try {
			const res = await this.client.listContextTemplates(props);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}
}
